/**
 * Lawn Irrigation Dashboard data shapes.
 *
 * These typedefs mirror the `lawn_*` SQLite tables one field at a time, so
 * swapping the data layer between mock data and the API is a data-layer change
 * rather than a UI rewrite. Only primitives cross this boundary: strings,
 * numbers, booleans, ISO date strings, and string foreign keys.
 *
 * Runtime exports at the bottom are the allowed value sets, kept here so the
 * forms, pins, and validation all read from one place.
 */

/**
 * Backed by `lawn_properties`.
 *
 * @typedef {Object} Property
 * @property {string} id
 * @property {string} name
 * @property {string} address
 * @property {string} imageUrl - yard photo the pins are placed on; empty means use placeholder art
 * @property {string} createdAt - ISO date
 */

/**
 * Backed by `lawn_sensors`.
 *
 * @typedef {Object} Sensor
 * @property {string} id
 * @property {string} propertyId
 * @property {string} name
 * @property {"soil_moisture"|"temperature"|"generic"} type
 * @property {number} xPct - 0-100, horizontal position on the property image
 * @property {number} yPct - 0-100, vertical position on the property image
 * @property {number} batteryPct - 0-100
 * @property {number|null} moisturePct - null when the sensor has never reported
 * @property {"online"|"offline"|"low_battery"} status
 * @property {string} lastReadingAt - ISO date
 */

/**
 * Backed by `lawn_sensor_readings`. Drives the sensor detail sparkline.
 *
 * @typedef {Object} SensorReading
 * @property {string} recordedAt - ISO date
 * @property {number|null} moisturePct
 */

/**
 * Backed by `lawn_forecast_days`.
 *
 * @typedef {Object} ForecastDay
 * @property {string} id
 * @property {string} propertyId
 * @property {string} date - ISO date
 * @property {"sunny"|"cloudy"|"rain"|"storms"} condition
 * @property {number} highF
 * @property {number} lowF
 * @property {number} precipChancePct
 * @property {boolean} rainSkipRecommended
 */

/**
 * Backed by `lawn_ordinances`.
 *
 * @typedef {Object} Ordinance
 * @property {string} id
 * @property {string} propertyId
 * @property {string} municipality
 * @property {"odd_even"|"day_of_week"|"time_window"} ruleType
 * @property {string[]} allowedDays - e.g. ["Monday", "Wednesday"] or ["odd"]
 * @property {string|null} allowedTimeStart - "HH:MM"
 * @property {string|null} allowedTimeEnd - "HH:MM"
 * @property {string} notes
 */

/**
 * Backed by `lawn_dashboard_widgets`. One row per tile in the grid, so the
 * customizable layout is already shaped like its future table.
 *
 * @typedef {Object} DashboardWidget
 * @property {string} id
 * @property {"property_view"|"forecast"|"ordinance"|"sensor_detail"|"sensor_list"} widgetType
 * @property {number} x - grid column (0-11)
 * @property {number} y - grid row
 * @property {number} width - column span
 * @property {number} height - row span
 * @property {string} configJson - widget-specific settings, JSON-encoded
 */

/**
 * Everything the dashboard needs in one payload.
 *
 * @typedef {Object} LawnDashboardData
 * @property {Property[]} properties
 * @property {Property|null} property
 * @property {Sensor[]} sensors
 * @property {ForecastDay[]} forecast
 * @property {Ordinance|null} ordinance
 * @property {DashboardWidget[]} widgets
 */

export const SENSOR_TYPES = ['soil_moisture', 'temperature', 'generic'];

export const SENSOR_TYPE_LABELS = {
  soil_moisture: 'Soil moisture',
  temperature: 'Temperature',
  generic: 'Generic',
};

export const SENSOR_STATUSES = ['online', 'offline', 'low_battery'];

export const SENSOR_STATUS_LABELS = {
  online: 'Online',
  offline: 'Offline',
  low_battery: 'Low battery',
};

export const FORECAST_CONDITIONS = ['sunny', 'cloudy', 'rain', 'storms'];

export const ORDINANCE_RULE_TYPES = ['odd_even', 'day_of_week', 'time_window'];

export const WIDGET_TYPES = [
  'property_view',
  'forecast',
  'ordinance',
  'sensor_detail',
  'sensor_list',
];

export const WIDGET_TYPE_LABELS = {
  property_view: 'Property view',
  forecast: 'Forecast',
  ordinance: 'Watering ordinance',
  sensor_detail: 'Sensor detail',
  sensor_list: 'Sensor list',
};

/** Default size and placement used when a widget is added back to the grid. */
export const WIDGET_DEFAULT_LAYOUT = {
  property_view: { width: 7, height: 9 },
  forecast: { width: 5, height: 5 },
  ordinance: { width: 5, height: 4 },
  sensor_detail: { width: 5, height: 7 },
  sensor_list: { width: 7, height: 7 },
};

export const GRID_COLUMNS = 12;
